import React, { useEffect, useState } from 'react';
import { IndiaMap, StateData } from '@vishalvoid/react-india-map';
import ComponentWrapper from '../components/utils/ComponentWrapper';
import { FaMapMarkedAlt, FaProjectDiagram, FaCity } from 'react-icons/fa';
import { BsBuilding, BsPeopleFill, BsInfoCircleFill } from 'react-icons/bs';
import { MdOutlineLocationOn } from 'react-icons/md';

interface CustomStateData {
  name: string;
  capital: string;
  population?: string;
  description?: string;
  projectCount: number;
  completedProjects: number;
  ongoingProjects: number;
  topCities: string[];
  revenueGenerated: string;
}

const States: React.FC = () => {
  const [selectedState, setSelectedState] = useState<CustomStateData | null>(null);

  // Enhanced state data with more details including project information
  const stateData: StateData[] = [
    {
      id: 'IN-MH',
      customData: {
        name: 'Maharashtra',
        capital: 'Mumbai',
        population: '123 million',
        description: 'Economic powerhouse of India with diverse industries',
        projectCount: 142,
        completedProjects: 98,
        ongoingProjects: 44,
        topCities: ['Mumbai', 'Pune', 'Nagpur', 'Nashik'],
        revenueGenerated: '₹342 Crores',
      },
    },
    {
      id: 'IN-DL',
      customData: {
        name: 'Delhi',
        capital: 'New Delhi',
        population: '31 million',
        description: 'Capital region and major political center',
        projectCount: 87,
        completedProjects: 54,
        ongoingProjects: 33,
        topCities: ['New Delhi', 'North Delhi', 'South Delhi', 'East Delhi'],
        revenueGenerated: '₹187 Crores',
      },
    },
    {
      id: 'IN-TN',
      customData: {
        name: 'Tamil Nadu',
        capital: 'Chennai',
        population: '77 million',
        description: 'Leading industrial and cultural hub in South India',
        projectCount: 93,
        completedProjects: 61,
        ongoingProjects: 32,
        topCities: ['Chennai', 'Coimbatore', 'Madurai', 'Salem'],
        revenueGenerated: '₹214 Crores',
      },
    },
    {
      id: 'IN-KA',
      customData: {
        name: 'Karnataka',
        capital: 'Bengaluru',
        population: '68 million',
        description: 'IT hub of India and tech innovation center',
        projectCount: 125,
        completedProjects: 89,
        ongoingProjects: 36,
        topCities: ['Bengaluru', 'Mysuru', 'Hubli', 'Mangaluru'],
        revenueGenerated: '₹278 Crores',
      },
    },
    {
      id: 'IN-GJ',
      customData: {
        name: 'Gujarat',
        capital: 'Gandhinagar',
        population: '70 million',
        description: 'Major business and manufacturing hub',
        projectCount: 110,
        completedProjects: 72,
        ongoingProjects: 38,
        topCities: ['Ahmedabad', 'Surat', 'Vadodara', 'Rajkot'],
        revenueGenerated: '₹235 Crores',
      },
    },
    {
      id: 'IN-UP',
      customData: {
        name: 'Uttar Pradesh',
        capital: 'Lucknow',
        population: '237 million',
        description: 'Most populous state with rich cultural heritage',
        projectCount: 103,
        completedProjects: 67,
        ongoingProjects: 36,
        topCities: ['Lucknow', 'Kanpur', 'Agra', 'Varanasi'],
        revenueGenerated: '₹189 Crores',
      },
    },
  ];

  // Updated map style with higher contrast colors for better border visibility
  const mapStyle = {
    backgroundColor: '#F0F0F0',
    hoverColor: '#4B5563',
    borderColor: '#111827',
    activeColor: '#F3F4F6',
    tooltipConfig: {
      backgroundColor: '#333333',
      textColor: '#ffffff',
      showTootip: true,
    },
  };

  // Keep track of hovered state to maintain tooltip on click
  const handleStateHover = (stateId: string) => {
    setHoveredState(stateId);
  };

  const handleStateClick = (stateId: string, stateInfo?: StateData) => {
    if (stateInfo?.customData) {
      setSelectedState(stateInfo.customData as CustomStateData);

      // Keep tooltip visible after click - create a small delay to ensure the tooltip stays visible
      const stateElement = document.getElementById(stateId);
      if (stateElement) {
        // Force tooltip to stay after click
        setTimeout(() => {
          // This keeps the tooltip visible after click by triggering a mouseenter event
          const enterEvent = new MouseEvent('mouseenter', {
            bubbles: true,
            cancelable: true,
          });
          stateElement.dispatchEvent(enterEvent);
        }, 100);
      }
    }
  };

  useEffect(() => {
    // Set default selected state
    if (stateData.length > 0 && stateData[0].customData) {
      setSelectedState(stateData[0].customData as CustomStateData);
    }

    // Add CSS to remove focus outlines but preserve borders
    const style = document.createElement('style');
    style.innerHTML = `
      path:focus, svg *:focus {
        outline: none !important;
        fill: inherit !important;
        stroke: #111827 !important;
        stroke-width: 1.5px !important;
      }
      path[aria-selected="true"] {
        fill: inherit !important;
        stroke: #111827 !important;
        stroke-width: 1.5px !important;
      }
      .react-india-map-svg path {
        stroke: #111827 !important;
        stroke-width: 1.5px !important;
      }
    `;
    document.head.appendChild(style);

    return () => {
      document.head.removeChild(style);
    };
  }, []);

  // Calculate total project count
  const totalProjects = stateData.reduce(
    (sum, state) => sum + (state.customData as CustomStateData)?.projectCount || 0,
    0
  );

  return (
    <ComponentWrapper icon={<MdOutlineLocationOn size={30} />} title="State Projects Dashboard">
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Map and Summary Section */}
        <div className="lg:col-span-2">
          {/* Summary Cards */}
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4 mb-6">
            <div className="bg-blue-50 rounded-lg p-4 border border-blue-100 shadow-sm">
              <div className="flex items-center justify-between">
                <h3 className="text-blue-800 font-medium">Total States</h3>
                <FaMapMarkedAlt className="text-blue-500" size={20} />
              </div>
              <p className="text-2xl font-bold text-blue-700 mt-2">{stateData.length}</p>
            </div>

            <div className="bg-green-50 rounded-lg p-4 border border-green-100 shadow-sm">
              <div className="flex items-center justify-between">
                <h3 className="text-green-800 font-medium">Total Projects</h3>
                <FaProjectDiagram className="text-green-500" size={20} />
              </div>
              <p className="text-2xl font-bold text-green-700 mt-2">{totalProjects}</p>
            </div>

            <div className="bg-purple-50 rounded-lg p-4 border border-purple-100 shadow-sm">
              <div className="flex items-center justify-between">
                <h3 className="text-purple-800 font-medium">Avg. per State</h3>
                <BsInfoCircleFill className="text-purple-500" size={20} />
              </div>
              <p className="text-2xl font-bold text-purple-700 mt-2">
                {Math.round(totalProjects / stateData.length)}
              </p>
            </div>

            <div className="bg-amber-50 rounded-lg p-4 border border-amber-100 shadow-sm">
              <div className="flex items-center justify-between">
                <h3 className="text-amber-800 font-medium">Top State</h3>
                <BsBuilding className="text-amber-500" size={20} />
              </div>
              <p className="text-lg font-bold text-amber-700 mt-2">Maharashtra</p>
              <p className="text-sm text-amber-600">142 projects</p>
            </div>
          </div>

          {/* Map container - Add a specific background for better contrast */}
          <div className="border border-gray-300 p-5 rounded-lg shadow-md bg-white mb-6 relative">
            <IndiaMap
              stateData={stateData}
              mapStyle={mapStyle}
              onStateHover={handleStateHover}
              onStateClick={handleStateClick}
            />

            {/* React Tooltip */}
            {/* <Tooltip
              id="state-tooltip"
              className="z-[9999]"
              style={{
                backgroundColor: 'rgba(31, 41, 55, 0.95)',
                color: 'white',
                borderRadius: '0.5rem',
                padding: '0.75rem',
                boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.2), 0 4px 6px -2px rgba(0, 0, 0, 0.1)',
                maxWidth: '16rem',
              }}
              opacity={1}
              place="top"
              delayShow={0}
              float={true}
              clickable={true} // Make tooltip clickable so it doesn't disappear immediately on click
              render={({ content }) => (
                <div dangerouslySetInnerHTML={{ __html: content as string }} />
              )}
            /> */}
          </div>

          {/* Project Distribution by State */}
          <div className="border border-gray-200 p-5 rounded-lg shadow-md bg-white">
            <h3 className="text-lg font-semibold mb-4">Project Distribution by State</h3>
            <div className="space-y-4">
              {stateData.map(state => {
                const stateInfo = state.customData as CustomStateData;
                const completionPercentage = Math.round(
                  (stateInfo.completedProjects / stateInfo.projectCount) * 100
                );

                return (
                  <div key={state.id} className="flex items-center space-x-4">
                    <div className="w-24 font-medium text-gray-700 truncate" title={stateInfo.name}>
                      {stateInfo.name}
                    </div>
                    <div className="flex-grow bg-gray-200 rounded-full h-4">
                      <div
                        className="bg-blue-600 h-4 rounded-full"
                        style={{ width: `${completionPercentage}%` }}
                      ></div>
                    </div>
                    <div className="w-24 text-sm text-gray-500">
                      {stateInfo.projectCount} projects
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        {/* State Details Panel */}
        <div className="bg-white rounded-lg border border-gray-200 shadow-md p-5 h-fit sticky top-5">
          {selectedState ? (
            <>
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-2xl font-bold text-gray-800">{selectedState.name}</h2>
                <div className="bg-blue-100 text-blue-800 text-xs px-2 py-1 rounded-full">
                  {selectedState.projectCount} Projects
                </div>
              </div>

              <div className="border-b pb-4 mb-4">
                <div className="flex items-center text-gray-700 mb-2">
                  <FaCity className="mr-2" />
                  <span className="font-medium">Capital:</span>
                  <span className="ml-2">{selectedState.capital}</span>
                </div>

                <div className="flex items-center text-gray-700 mb-4">
                  <BsPeopleFill className="mr-2" />
                  <span className="font-medium">Population:</span>
                  <span className="ml-2">{selectedState.population}</span>
                </div>

                <p className="text-gray-600">{selectedState.description}</p>
              </div>

              {/* Project Statistics */}
              <h3 className="font-semibold text-lg mb-3">Project Statistics</h3>
              <div className="grid grid-cols-3 gap-3 mb-5">
                <div className="bg-green-50 rounded-lg p-3 text-center">
                  <div className="text-green-700 text-lg font-bold">
                    {selectedState.completedProjects}
                  </div>
                  <div className="text-green-600 text-xs">Completed</div>
                </div>
                <div className="bg-yellow-50 rounded-lg p-3 text-center">
                  <div className="text-yellow-700 text-lg font-bold">
                    {selectedState.ongoingProjects}
                  </div>
                  <div className="text-yellow-600 text-xs">Ongoing</div>
                </div>
                <div className="bg-blue-50 rounded-lg p-3 text-center">
                  <div className="text-blue-700 text-lg font-bold">
                    {selectedState.revenueGenerated}
                  </div>
                  <div className="text-blue-600 text-xs">Revenue</div>
                </div>
              </div>

              {/* Key Cities */}
              <h3 className="font-semibold text-lg mb-3">Key Project Cities</h3>
              <div className="flex flex-wrap gap-2 mb-5">
                {selectedState.topCities.map((city, index) => (
                  <div
                    key={index}
                    className="bg-gray-100 rounded-full px-3 py-1 text-sm text-gray-700"
                  >
                    {city}
                  </div>
                ))}
              </div>

              {/* Action Buttons */}
              <div className="mt-6 space-x-3">
                <button className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 transition-colors">
                  View All Projects
                </button>
                <button className="border border-gray-300 text-gray-700 px-4 py-2 rounded-md hover:bg-gray-50 transition-colors">
                  Export Report (CSV)
                </button>
              </div>
            </>
          ) : (
            <p className="text-gray-500">Select a state to view details</p>
          )}
        </div>
      </div>
    </ComponentWrapper>
  );
};

export default States;
